import { ProjectStagesView } from "@mj/shared-ui";
import { Button, Card, Tag, Tooltip, Checkbox, Typography } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import { produce } from "immer";
import React from "react";
import { toast } from "react-toastify";
import { areEqual, FixedSizeList, ListChildComponentProps } from "react-window";

import { SwitchButton } from "@/components/ui/SwitchButton";
import { useBulkUpdateTasks } from "@/hooks/forecasting";
import { useElementHeight } from "@/hooks/useElementHeight";
import { ISelectOption } from "@/types";
import {
  IForecastingCentralRepoRes,
  IForecastingRes,
  ITask,
  StageAndTask
} from "@/types/forecasting";

const { Title, Text } = Typography;

type IViewType = "tasks" | "stage";

/**
 * A month can hold thousands of forecasted tasks, so each week column is
 * windowed — only the cards in view are mounted. Every card is the same
 * height so the column can be a `FixedSizeList`; if you add content to the
 * card, update this constant.
 */
const TASK_ROW_HEIGHT = 128;
const TASK_ROW_GAP = 6;

/** Stable identity for a week with nothing forecasted in it. */
const NO_STAGES: StageAndTask[] = [];

interface ITaskStatusChange {
  week: number;
  stageIndex: number;
  taskIndex: number;
  status: string;
}

/** One card in a week column, with its position in that week's stages. */
interface IWeekTaskRow {
  stage: StageAndTask;
  task: ITask;
  stageIndex: number;
  taskIndex: number;
}

interface ITaskRowData {
  rows: IWeekTaskRow[];
  week: number;
  isCurrentMonth: boolean;
  onStatusChange: (change: ITaskStatusChange) => void;
}

const getTaskBackgroundColor = (status: string) => {
  switch (status) {
    case "Completed":
      return "#f6ffed"; // Light green
    case "In Progress":
      return "#fff7e6"; // Light orange
    case "Not Started":
      return "#f0f5ff"; // Light blue
    default:
      return "#ffffff"; // White (no status)
  }
};

const getTaskBorderColor = (status: string) => {
  switch (status) {
    case "Completed":
      return "#b7eb8f"; // Green
    case "In Progress":
      return "#ffd591"; // Orange
    case "Not Started":
      return "#adc6ff"; // Blue
    default:
      return "#d9d9d9"; // Gray (no status)
  }
};

export const TasksStageView = ({
  latestVersion,
  selectedProject,
  selectedMonth,
  monthForecastData,
  projectCentralRepoLoading,
  projectCentralRepoData
}: {
  latestVersion?: string | null;
  selectedProject?: ISelectOption;
  selectedMonth?: string;
  monthForecastData: IForecastingRes;
  projectCentralRepoLoading: boolean;
  projectCentralRepoData?: IForecastingCentralRepoRes;
}) => {
  const [viewType, setViewType] = React.useState<IViewType>("tasks");

  return (
    <Card
      className="border-none"
      classNames={{
        body: "!p-4"
      }}
    >
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-baseline gap-2">
            <Title level={5}>
              {viewType === "tasks" ? "Tasks View" : "Stage View"}
            </Title>
            {latestVersion && (
              <span className="text-gray-500">(Version {latestVersion})</span>
            )}
          </div>
          {viewType === "tasks" && selectedMonth && (
            <p className="text-[#1e293b]">
              {dayjs(selectedMonth).format("MMMM YYYY")}
            </p>
          )}
          {viewType === "stage" && (
            <p className="text-[#1e293b]">Project stages status</p>
          )}
        </div>
        <SwitchButton
          value={viewType}
          options={[
            {
              value: "tasks",
              label: "Tasks"
            },
            {
              value: "stage",
              label: "Stage"
            }
          ]}
          onChange={(val) => setViewType(val as IViewType)}
        />
      </div>

      <div>
        {viewType === "tasks" && (
          <TasksView
            selectedProject={selectedProject!}
            selectedMonth={selectedMonth!}
            monthForecastData={monthForecastData!}
            projectCentralRepoData={projectCentralRepoData!}
          />
        )}
        {viewType === "stage" && (
          <ProjectStagesView
            loading={projectCentralRepoLoading}
            data={projectCentralRepoData}
          />
        )}
      </div>
    </Card>
  );
};

const TasksView = ({
  selectedProject,
  selectedMonth,
  monthForecastData,
  projectCentralRepoData
}: {
  selectedProject: ISelectOption;
  selectedMonth: string;
  monthForecastData: IForecastingRes;
  projectCentralRepoData: IForecastingCentralRepoRes;
}) => {
  const [weekStagesAndTasks, setWeekStagesAndTasks] =
    React.useState<Record<number, StageAndTask[]>>();

  const bulkUpdateTasks = useBulkUpdateTasks();

  const centralTaskStatuses = React.useMemo(() => {
    const map = new Map<string, string>();
    projectCentralRepoData?.groundworks?.plots?.forEach((plot) => {
      plot.stages?.forEach((stage) => {
        stage.tasks?.forEach((task) => {
          map.set(task.taskId, task.currentStatus);
        });
      });
    });
    projectCentralRepoData?.roadsAndSewers?.runs?.forEach((run) => {
      run.stages?.forEach((stage) => {
        stage.tasks?.forEach((task) => {
          map.set(task.taskId, task.currentStatus);
        });
      });
    });
    return map;
  }, [projectCentralRepoData]);

  React.useEffect(() => {
    const allStagesAndTasks = [
      ...(monthForecastData?.groundworks?.StagesAndTasks ?? []),
      ...(monthForecastData?.roadAndSewers?.StagesAndTasks ?? [])
    ];

    if (allStagesAndTasks.length > 0) {
      const mappings: Record<number, StageAndTask[]> = {};
      allStagesAndTasks.forEach((stageAndTasks) => {
        const parsedWeek = stageAndTasks?.SelectedWeek?.match(/W(\d+)/i)?.[1];
        if (parsedWeek) {
          const weekNum = Number(parsedWeek);
          if (!mappings[weekNum]) {
            mappings[weekNum] = [];
          }
          mappings[weekNum].push({
            ...stageAndTasks,
            Tasks: stageAndTasks.Tasks.map((task) => ({
              ...task,
              status: centralTaskStatuses.get(task.taskId) ?? task.status
            }))
          });
        }
      });
      setWeekStagesAndTasks(mappings);
    }
  }, [monthForecastData, centralTaskStatuses]);

  const month = dayjs(selectedMonth);
  const isCurrentMonth = month.format("YYYY-MM") === dayjs().format("YYYY-MM");

  const m = month.month() + 1;
  const y = month.year();
  const numOfWeeks = Math.ceil(new Date(y, m, 0).getDate() / 7);

  const monthWeeks: { month: number; year: number; week: number }[] = [];
  for (let week = 1; week <= numOfWeeks; week++) {
    monthWeeks.push({ month: m, year: y, week });
  }

  const today = new Date();
  const currentMonthWeek = `${today.getFullYear()}-${String(
    today.getMonth() + 1
  ).padStart(2, "0")}-W${Math.ceil(today.getDate() / 7)}`;

  // Functional update keeps this callback stable, and immer's structural
  // sharing leaves untouched weeks with their exact arrays — so only the
  // column that changed re-renders.
  const handleTaskStatusChange = React.useCallback(
    ({ week, stageIndex, taskIndex, status }: ITaskStatusChange) => {
      setWeekStagesAndTasks((prev) =>
        produce(prev, (draft) => {
          if (draft) draft[week][stageIndex].Tasks[taskIndex].status = status;
        })
      );
    },
    []
  );

  const originalTaskStatuses = centralTaskStatuses;

  const handleSubmit = async () => {
    const updates: {
      taskId: string;
      currentStatus: "Completed" | "Not Started";
      moveToMonth: string | null;
    }[] = [];

    if (weekStagesAndTasks) {
      Object.values(weekStagesAndTasks).forEach((stages) => {
        stages.forEach((stage) => {
          stage.Tasks?.forEach((task) => {
            const originalStatus = originalTaskStatuses.get(task.taskId);
            if (originalStatus !== task.status) {
              updates.push({
                taskId: task.taskId,
                currentStatus: task.status as "Completed" | "Not Started",
                moveToMonth: null
              });
            }
          });
        });
      });
    }

    if (updates.length === 0) {
      toast.info("No tasks have been changed.");
      return;
    }

    await bulkUpdateTasks.mutateAsync({
      project: selectedProject?.description,
      payload: { opportunityNo: selectedProject?.opportunityNo, updates }
    });
    toast.success("Tasks updated successfully.");
  };

  return (
    <div className="mt-3">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${numOfWeeks}, minmax(0, 1fr))`,
          gap: 16
        }}
      >
        {monthWeeks.map(({ month, year, week }) => {
          const monthWeek = `${year}-${String(month).padStart(
            2,
            "0"
          )}-W${week}`;

          return (
            <WeekColumn
              key={monthWeek}
              week={week}
              isCurrentWeek={monthWeek === currentMonthWeek}
              isCurrentMonth={isCurrentMonth}
              stages={weekStagesAndTasks?.[week] ?? NO_STAGES}
              onStatusChange={handleTaskStatusChange}
            />
          );
        })}
      </div>
      {isCurrentMonth && (
        <div className="flex justify-end mt-4">
          <Button
            size="large"
            type="primary"
            loading={bulkUpdateTasks.isPending}
            onClick={handleSubmit}
            className="bg-blue-600"
          >
            Submit
          </Button>
        </div>
      )}
    </div>
  );
};

const WeekColumn = React.memo(
  ({
    week,
    isCurrentWeek,
    isCurrentMonth,
    stages,
    onStatusChange
  }: {
    week: number;
    isCurrentWeek: boolean;
    isCurrentMonth: boolean;
    stages: StageAndTask[];
    onStatusChange: (change: ITaskStatusChange) => void;
  }) => {
    const [listRef, listHeight] = useElementHeight();

    const rows = React.useMemo(
      () =>
        stages.flatMap((stage, stageIndex) =>
          (stage.Tasks ?? []).map((task, taskIndex) => ({
            stage,
            task,
            stageIndex,
            taskIndex
          }))
        ),
      [stages]
    );

    const itemData = React.useMemo<ITaskRowData>(
      () => ({ rows, week, isCurrentMonth, onStatusChange }),
      [rows, week, isCurrentMonth, onStatusChange]
    );

    return (
      <div
        className={classNames(
          "flex flex-col min-h-[400px] max-h-[600px] h-[600px] rounded-xl overflow-hidden",
          "border border-solid border-[#d9d9d9]"
        )}
      >
        {/* Week Header - Fixed */}
        <div
          className={classNames(
            "p-3 pb-2",
            isCurrentWeek ? "bg-[#e6f7ff]" : "bg-white"
          )}
        >
          <div className="flex justify-between items-center mb-1">
            <Text
              strong
              style={{
                color: isCurrentWeek ? "#1890ff" : undefined,
                fontSize: "13px"
              }}
            >
              Week {week}
            </Text>
            {isCurrentWeek && (
              <Tag color="blue" style={{ fontSize: "10px", margin: 0 }}>
                Current
              </Tag>
            )}
          </div>
        </div>

        {/* Tasks Container - Windowed */}
        {/* Each row's gap sits below its card, so the first card needs this
            top offset to match the spacing between the others */}
        <div className="flex-1 min-h-0 pt-1.5">
          <div ref={listRef} className="h-full">
            {listHeight > 0 && (
              <FixedSizeList
                height={listHeight}
                width="100%"
                itemCount={rows.length}
                itemSize={TASK_ROW_HEIGHT}
                itemData={itemData}
              >
                {TaskRow}
              </FixedSizeList>
            )}
          </div>
        </div>
      </div>
    );
  }
);
WeekColumn.displayName = "WeekColumn";

const TaskRow = React.memo(
  ({ index, style, data }: ListChildComponentProps<ITaskRowData>) => {
    const { rows, week, isCurrentMonth, onStatusChange } = data;
    const { stage, task, stageIndex, taskIndex } = rows[index];
    const isCompleted = task.status === "Completed";

    const changeStatus = (status: string) =>
      onStatusChange({ week, stageIndex, taskIndex, status });

    return (
      <div style={style} className="px-3">
        <div
          className="box-border px-2 py-1.5 rounded flex flex-col gap-1 overflow-hidden"
          style={{
            height: TASK_ROW_HEIGHT - TASK_ROW_GAP,
            background: getTaskBackgroundColor(task.status),
            border: `1px solid ${getTaskBorderColor(task.status)}`
          }}
        >
          <Tooltip
            title={`${task.taskName} (${stage.Stage})`}
            placement="topLeft"
          >
            <Text
              ellipsis
              style={{
                fontSize: "12px",
                fontWeight: 500,
                display: "block",
                textDecoration: isCompleted ? "line-through" : "none",
                color: isCompleted ? "#52c41a" : undefined
              }}
            >
              {task.taskName}
            </Text>
          </Tooltip>
          {/* One non-wrapping line, so the card height stays fixed */}
          <div className="flex items-center gap-1 mt-0.5 min-w-0">
            <Tag
              color="geekblue"
              className="shrink-0"
              style={{ fontSize: "10px", margin: 0 }}
            >
              P{stage.Plot}
            </Tag>
            <Text
              type="secondary"
              ellipsis
              className="min-w-0"
              style={{ fontSize: "10px" }}
            >
              {stage.Stage}
            </Text>
          </div>
          <div className="flex flex-col gap-1 pt-1 mt-0.5">
            <Checkbox
              checked={task.status === "Not Started"}
              disabled={!isCurrentMonth || isCompleted}
              onChange={() => changeStatus("Not Started")}
              style={{ fontSize: "11px" }}
            >
              <Text style={{ fontSize: "10px", color: "#595959" }}>
                Not Started
              </Text>
            </Checkbox>
            <Checkbox
              checked={isCompleted}
              disabled={!isCurrentMonth || isCompleted}
              onChange={() => changeStatus("Completed")}
              style={{ fontSize: "11px" }}
            >
              <Text style={{ fontSize: "10px", color: "#52c41a" }}>
                Completed
              </Text>
            </Checkbox>
          </div>
        </div>
      </div>
    );
  },
  areEqual
);
TaskRow.displayName = "TaskRow";
